/* ============================================================
   dd-bundle-builder.js

   Owns the state machine for the Inventory Builder section
   (sections/dd-bundle-builder.liquid). Surfaces are the home page
   and /pages/inventory-builder — both render the same section, so
   one shared localStorage key keeps panel state consistent across
   entrypoints.

   Responsibilities:
     1. Hold the panel state (which variants the customer has
        staged, their qtys, the running total).
     2. Hydrate on init from BOTH localStorage (panel composition)
        AND /cart.js (canonical line qtys). The panel is a staging
        area — items live in state + localStorage until the customer
        commits via "Add Package to Cart". So a localStorage line
        WITHOUT a matching cart line is the normal case (staged-only)
        and is preserved. A line that appears in BOTH places adopts
        the cart's qty/price (cart wins on overlap, which only
        happens if the same variant was added via a non-panel path).
     3. Listen for `dd-card2:add-to-bundle` events dispatched by
        `dd-card2.js` when a card has `data-dd-cta-mode="bundle"`.
        Each event adds/increments one variant in panel state.
     4. Render the panel (line items, qty steppers, subtotal,
        threshold meter).
     5. On "Add Package to Cart": batch-add all panel items via
        ddCartHelpers.cartMutate('/cart/add.js', { items: [...] }).
        Then, if (totalQty >= threshold): read
        /cart.js for current discount_codes, build a merged
        comma-string preserving every other code, and POST that to
        /cart/update.js. NEVER use the DOM-coupled helpers in
        dd-discount-code.js — those are tied to the cart-drawer
        chip badges and don't exist on the builder page.
     6. On `cart:updated`: if our own bundle code was applied but
        cart qty has dropped below threshold, remove it (same JSON-
        merge approach in reverse).
     7. Single-writer mutex: a `_isMutating` flag set true before
        any mutation and cleared in `finally`. While true, ignore
        incoming `cart:updated` and `visibilitychange` rehydrate
        triggers. Our own mutations tag their `cart:updated`
        publishes with `source: 'dd-bundle-builder'` and we ignore
        events carrying that source to prevent self-trigger loops.

   Why NO /discount/CODE cookie path:
     Earlier draft used `fetch('/discount/CODE', { redirect: 'manual' })`
     (the pattern from dd-welcome-sticky.js). Codex Pass 8 flagged
     it: the cookie outlives panel edits — if the customer adds 10
     items, gets the cookie, then removes 5 in the drawer, the
     cookie stays applied and the customer either keeps a discount
     they shouldn't have or hits a checkout rejection. Cart-level
     /cart/update.js is symmetric: apply AND remove use the same
     mechanism and `cart.discount_codes` is the single source of
     truth.

   VIP "take the better" handling:
     Smile VIP tiers (DeadHead 5%, Army 10%, Inner Circle 20%)
     are storefront-rendered automatic discounts on every line.
     The bundle codes (BUNDLE5/10/20) are configured in admin as
     non-combinable with product discounts. At reconcile time we
     pick MAX(vipPct, bundlePct): if VIP already meets/beats the
     earned bundle tier we don't apply our code at all (Inner
     Circle never sees a ladder). Earlier drafts gated this on
     customer.tags contains 'wholesale' and a Regios runtime
     probe — both removed: the 'wholesale' tag is universal on
     this store and Smile vip_tier_name (server-rendered) is the
     authoritative signal.
   ============================================================ */

(function () {
  'use strict';

  if (window.__ddBundleBuilderInit) return;
  window.__ddBundleBuilderInit = true;

  var STORAGE_KEY_PREFIX = 'dd-bundle-builder-v1:';
  /* Guest persistence key. Same prefix family as the customer-scoped
     keys; the literal "guest" suffix is reserved (no real customer id
     is the string "guest" — Shopify customer ids are numeric). */
  var GUEST_STORAGE_KEY = STORAGE_KEY_PREFIX + 'guest';
  /* Guest-package TTL. After 7 days of inactivity we discard the
     stored panel composition on hydrate. This keeps stale variants /
     stale pricing from re-rendering for a stylist who browsed weeks
     ago and never came back. Customer-scoped entries are NOT TTL'd
     because (a) Shopify already keeps the canonical cart cookie
     fresh for logged-in customers, and (b) variant-availability
     drift is corrected by the existing cart-cross-check in
     _rehydrateFromCart() — see header comment §2. */
  var GUEST_STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
  var EVENT_ADD = 'dd-card2:add-to-bundle';
  var SOURCE_TAG = 'dd-bundle-builder';
  var SECTIONS_PARAM = 'cart-drawer,cart-icon-bubble';
  var CART_FRESH_MAX_AGE = 1500;

  /* ── Helpers ───────────────────────────────────────────────── */

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else { fn(); }
  }

  function safeRead(key) {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); }
    catch (_e) { return null; }
  }

  function safeWrite(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); return true; }
    catch (_e) { return false; }
  }

  function getCustomerId() {
    var id = document.documentElement.dataset.glassCustomerId;
    if (!id || id === '0') return '';
    return String(id);
  }

  function formatMoney(cents) {
    if (window.ddCartHelpers && typeof window.ddCartHelpers.formatMoney === 'function') {
      try { return window.ddCartHelpers.formatMoney(cents); } catch (_e) {}
    }
    var fmt = window.shopMoneyFormat
      ? String(window.shopMoneyFormat).replace(/<[^>]*>/g, '')
      : '${{amount}}';
    var amount = (Number(cents || 0) / 100).toFixed(2);
    var withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var out = fmt
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(Number(cents || 0) / 100).toString())
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    return out.replace(/([.,])00(?!\d)/, '');
  }

  /* Normalize a Shopify code for comparison — case-insensitive,
     trimmed. Matches the contract of `normalizeCode` in
     dd-discount-code.js without depending on its DOM coupling. */
  function normalizeCode(c) {
    return String(c == null ? '' : c).trim().toUpperCase();
  }

  /* Build a comma-separated discount string for /cart/update.js
     that preserves every existing applied code except `excludeCode`
     (case-insensitive), then optionally appends `addCode` if not
     already present. Reads cart.discount_codes — the canonical
     Shopify shape [{ code, amount, type, applicable }, ...]. */
  function buildDiscountParam(cart, opts) {
    opts = opts || {};
    var exclude = opts.excludeCode ? normalizeCode(opts.excludeCode) : '';
    var addCode = opts.addCode || '';
    var addNorm = normalizeCode(addCode);
    var existing = (cart && Array.isArray(cart.discount_codes)) ? cart.discount_codes : [];
    var keep = [];
    var seen = Object.create(null);
    for (var i = 0; i < existing.length; i++) {
      var raw = existing[i] && existing[i].code;
      if (!raw) continue;
      var norm = normalizeCode(raw);
      if (exclude && norm === exclude) continue;
      if (addNorm && norm === addNorm) continue; /* avoid duplicate */
      if (seen[norm]) continue;
      seen[norm] = true;
      keep.push(String(raw).trim());
    }
    if (addCode) keep.push(String(addCode).trim());
    return keep.join(',');
  }

  /* Inspect cart.discount_codes for a code (case-insensitive). */
  function cartHasApplicableCode(cart, code) {
    var target = normalizeCode(code);
    if (!cart || !Array.isArray(cart.discount_codes)) return false;
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var d = cart.discount_codes[i];
      if (!d) continue;
      if (normalizeCode(d.code) === target && d.applicable !== false) return true;
    }
    return false;
  }

  /* Sum the qty of cart line items whose variant_id is in the
     eligible-collection set. Shopify's admin discount is configured
     "min N items from collection X" — that's the security boundary.
     This client-side filter exists only so the theme UX doesn't try
     to apply BUNDLE20 when the customer has 10 random non-bundle
     items in cart (Shopify would reject and we'd show a noisy "not
     eligible" toast). When the set is empty (legacy section, hot
     reload race, etc.) we fall back to total cart qty so the
     pre-add gate degrades to "always attempt, let Shopify decide"
     — same behavior as before this filter existed.

     Note: the set holds VARIANT ids, not product ids. A product can
     have variants in and out of the bundle collection (in practice
     all variants are eligible, but Shopify's collection-by-variant
     model means we can't safely deduplicate by product). */
  function bundleCollectionQty(cart, eligibleSet) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    var hasFilter = eligibleSet && eligibleSet.size > 0;
    var n = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!it) continue;
      if (hasFilter && !eligibleSet.has(String(it.variant_id))) continue;
      n += Number(it.quantity) || 0;
    }
    return n;
  }

  /* Note: an earlier Regios runtime probe lived here. It read the
     RegiosDOPP v0 calculateDiscountedPrices() result to decide if
     the cart was wholesale-discounted. Removed because (a) the
     customer.tags 'wholesale' SSR hint was universal on this store
     and so always-true, and (b) Smile's server-rendered
     vip_tier_name (Inner Circle / Army / DeadHead) is now the
     authoritative pct signal driving reconcile. The probe added
     latency and a hard dependency on a third-party global that the
     ladder no longer needs. */

  /* ── Controller (one per section) ─────────────────────────── */

  function BundleBuilder(sectionEl) {
    this.el = sectionEl;

    /* Phase 2: tier ladder + VIP state. Each tier is
       { code, minQty, pct, label }. Sorted ascending by minQty for
       _resolveTier(); filtered to enabled (minQty>0, pct>0). */
    var rawTiers = [];
    try { rawTiers = JSON.parse(sectionEl.dataset.ddBundleTiers || '[]'); }
    catch (_e) { rawTiers = []; }
    this.tiers = (Array.isArray(rawTiers) ? rawTiers : [])
      .filter(function (t) { return t && t.code && Number(t.minQty) > 0 && Number(t.pct) > 0; })
      .map(function (t) {
        return {
          code: String(t.code).trim(),
          minQty: Number(t.minQty),
          pct: Number(t.pct),
          label: String(t.label || '')
        };
      })
      .sort(function (a, b) { return a.minQty - b.minQty; });

    /* Set of normalized ladder codes (uppercase, trimmed) for fast
       "is this code one of ours?" lookups during reconcile. */
    this.ourCodesNorm = this.tiers.map(function (t) { return normalizeCode(t.code); });

    this.vipState = sectionEl.dataset.ddBundleVipState || 'guest';
    this.vipPct = parseInt(sectionEl.dataset.ddBundleVipPct, 10) || 0;

    this.customerId = getCustomerId();
    /* Storage key resolution:
         signed-in → "dd-bundle-builder-v1:<customer_id>"   (no TTL)
         guest     → "dd-bundle-builder-v1:guest"           (7-day TTL)
       Guests get a stable shared key (not per-session) so a stylist
       who builds a package, navigates away, then returns finds it
       intact. _isPersistedKeyGuest gates the TTL on hydrate and
       triggers the one-time guest→customer migration in _hydrate. */
    this.storageKey = this.customerId
      ? STORAGE_KEY_PREFIX + this.customerId
      : GUEST_STORAGE_KEY;
    this._isPersistedKeyGuest = !this.customerId;

    /* Set of variant IDs (as strings) belonging to the bundle
       collection. Server-rendered so the theme can answer
       "is this variant bundle-eligible?" without an extra fetch.
       Empty set is the safe-degrade signal — see bundleCollectionQty. */
    this.eligibleVariantIds = new Set();
    var idsScript = sectionEl.querySelector('script[data-dd-bundle-eligible-variant-ids]');
    if (idsScript) {
      try {
        var ids = JSON.parse(idsScript.textContent || '[]');
        if (Array.isArray(ids)) ids.forEach(function (id) {
          if (id != null) this.add(String(id));
        }, this.eligibleVariantIds);
      } catch (_e) { /* invalid JSON → empty set → safe fallback */ }
    }

    /* Starter-package prefill. Server-rendered as an array of line
       objects matching the persisted-items shape — one per
       merchant-curated starter product's first in-stock variant.
       Inflated into state whenever the panel is empty on hydrate (a
       fresh slot OR one the visitor cleared back to zero), so the
       starter set returns on the next load after a clear. Normalized
       to a { [variantId]: line } map so it can ride the same
       inflateStoredItems path as a localStorage package. Null when the
       section rendered no prefill (feature off, no products
       configured, or none in stock). */
    this.prefillItems = null;
    var prefillScript = sectionEl.querySelector('script[data-dd-bundle-prefill]');
    if (prefillScript) {
      try {
        var pfArr = JSON.parse(prefillScript.textContent || '[]');
        if (Array.isArray(pfArr) && pfArr.length) {
          var pfMap = Object.create(null);
          pfArr.forEach(function (l) {
            if (l && l.id != null) pfMap[String(l.id)] = l;
          });
          if (Object.keys(pfMap).length) this.prefillItems = pfMap;
        }
      } catch (_e) { /* invalid JSON → no prefill */ }
    }

    this.panel = sectionEl.querySelector('[data-dd-bundle-panel]');
    this.linesEl = sectionEl.querySelector('[data-dd-bundle-lines]');
    this.emptyEl = sectionEl.querySelector('[data-dd-bundle-empty]');
    /* Skeleton placeholder block — 3 rows shown by default in both the
       guest and logged-in-empty states. We hide it as soon as the user
       has at least 1 line, restore it when the last line is removed.
       Toggling a single `hidden` attribute is one DOM mutation per
       transition; per-skeleton class toggles would cost three. */
    this.skeletonsEl = sectionEl.querySelector('[data-dd-bundle-skeletons]');
    /* Post-add confirmation bento — replaces the skeleton placeholders
       on the render that immediately follows a successful _batchAddToCart.
       Transient (not persisted); reset to skeletons the moment the
       customer stages a new item via _addItem. */
    this.confirmationEl = sectionEl.querySelector('[data-dd-bundle-confirmation]');
    this.isGuest = this.panel && this.panel.getAttribute('data-dd-bundle-is-guest') === 'true';
    /* Unified motivational progress line — replaces the old "qty / top"
       counter. JS rewrites this on every cart mutation via
       _renderProgress(); Liquid SSRs the qty=0 default per vip_state.
       Absent for Inner Circle (the IC callout fills the meter slot). */
    this.progressEl = sectionEl.querySelector('[data-dd-bundle-progress]');
    /* Phase 2 meter: the 3-stop ladder element. May be absent for
       Inner Circle (Liquid omits the markup entirely; IC sees the
       calm planning callout instead). */
    this.meterEl = sectionEl.querySelector('[data-dd-bundle-meter]');
    this.meterFillEl = sectionEl.querySelector('[data-dd-bundle-meter-fill]');
    this.icCalloutEl = sectionEl.querySelector('[data-dd-bundle-ic-callout]');
    this.subtotalEl = sectionEl.querySelector('[data-dd-bundle-subtotal]');
    this.subtotalCompareEl = sectionEl.querySelector('[data-dd-bundle-subtotal-compare]');
    this.savingsEl = sectionEl.querySelector('[data-dd-bundle-savings]');
    this.savingsAmountEl = sectionEl.querySelector('[data-dd-bundle-savings-amount]');
    this.savingsPctEl = sectionEl.querySelector('[data-dd-bundle-savings-pct]');
    this.ctaEl = sectionEl.querySelector('[data-dd-bundle-add-all]');
    this.ctaLabelEl = sectionEl.querySelector('[data-dd-bundle-cta-label]');
    this.statusEl = sectionEl.querySelector('[data-dd-bundle-status]');
    this.lineTemplate = sectionEl.querySelector('[data-dd-bundle-line-template]');

    this.state = {
      items: Object.create(null),  /* { [variantId]: line } */
      totalQty: 0,
      totalCents: 0,
      _isMutating: false
    };

    /* Per-product inventory cache. Each key is a product handle and the
       value is a Promise<Map<variantIdStr, { maxQty, untracked }>>.
       Used to enforce per-line stock caps so the panel can't promise
       30 units of a variant that only has 12 in stock. The underlying
       ddVariantLoader sessionStorage-caches its GraphQL response for
       5 minutes, so a re-open of the panel doesn't re-fetch.

       Why we don't carry inventory on the dd-card2:add-to-bundle
       event detail: dd-card2 doesn't currently expose quantityAvailable
       on its internal variant objects, and adding it there would
       require threading it through the seed → GraphQL refresh path.
       Doing the lookup here keeps the change localized and gives us
       a single chokepoint for the cap rule.

       Race UX: an Add fires _addItem() immediately so the line
       appears with no jank, then a microtask later the cap resolves
       and clamps line.qty if necessary (or removes the line if the
       variant is OOS). The user sees the optimistic insert, then a
       tiny adjustment + status nudge — preferable to either gating
       the click on a network round-trip or silently letting an
       over-cap line slip through to /cart/add.js failure. */
    this._inventoryCache = Object.create(null);

    /* Queue flag: if a foreign cart:updated arrives while we hold the
       mutex, set this and re-run reconcile in the in-flight mutation's
       .finally(). Never silently drop a reconcile request. */
    this._reconcilePending = false;

    this._bindEvents();
    this._hydrate();
  }

  /* ── Tier resolution & reconcile (Phase 2) ───────────────────
     The single point of truth for "which discount code should be on
     the cart right now?" Reconcile runs after every cart mutation
     that we know about and after every external cart:updated event.
     The contract is the §5.3 return-value table. */

  /* Returns the highest-min-qty tier matched by eligibleQty, or
     null. `this.tiers` is pre-sorted ascending in the constructor. */
  BundleBuilder.prototype._resolveTier = function (eligibleQty) {
    if (!Array.isArray(this.tiers) || !this.tiers.length) return null;
    var winner = null;
    for (var i = 0; i < this.tiers.length; i++) {
      if (eligibleQty >= this.tiers[i].minQty) winner = this.tiers[i];
    }
    return winner;
  };

  /* Build the next `discount=` parameter for /cart/update.js.

     Returns one of:
       • null  — no change needed (target matches present ladder state)
       • ''    — cart should have NO discount codes at all (Shopify's
                 documented way to clear all codes via `discount=`)
       • 'A,B,C'  — comma-separated state preserving every non-ladder
                    code and replacing ladder codes with at most one
                    target ladder code

     The vipPct < 20 gate is the central VIP-vs-bundle enforcement
     under the new stacking model: any non-IC customer gets the
     earned tier applied. BUNDLE5 / BUNDLE10 are configured in admin
     as stackable with product discounts; BUNDLE20 is non-combinable
     (Shopify takes max(VIP, 20%) = 20% automatically). IC customers
     are short-circuited because any bundle stack would exceed the
     25% margin cap. */
  BundleBuilder.prototype._reconcileDiscountCodes = function (cart, eligibleQty) {
    var ourCodesNorm = this.ourCodesNorm;

    /* Determine the target ladder code (or null). Stacking strategy:
       BUNDLE5 and BUNDLE10 are configured in admin to stack with
       Regios VIP product discounts (combines_with.product_discounts =
       true); BUNDLE20 replaces (combines_with.product_discounts =
       false), so Shopify picks the higher of VIP / BUNDLE20 at
       checkout — customer always ends at 20% when 100+ units land,
       regardless of VIP tier.

       Inner Circle (vipPct == 20): no bundle code is ever applied.
       They're already at the 20% storefront cap; BUNDLE5 or BUNDLE10
       stacking on top would put their effective discount at 25% or
       30%, which violates the 25% margin cap (20% IC + 1.5% rewards
       is already at 21.5%; any bundle stack pushes over).

       All other states (guest, logged_in, deadhead, army): apply the
       highest tier the eligible qty earns. Shopify enforces the
       stack-vs-replace behavior per-code via combines_with. */
    var targetTier = null;
    if (this.vipPct < 20) {
      var resolved = this._resolveTier(eligibleQty);
      if (resolved) {
        targetTier = resolved;
      }
    }
    var targetCode = targetTier ? targetTier.code : null;
    var targetNorm = targetCode ? normalizeCode(targetCode) : '';

    var existing = (cart && Array.isArray(cart.discount_codes)) ? cart.discount_codes : [];

    /* What ladder codes are currently on the cart? */
    var presentLadderCodes = [];
    for (var i = 0; i < existing.length; i++) {
      var d = existing[i];
      if (!d || !d.code) continue;
      if (ourCodesNorm.indexOf(normalizeCode(d.code)) !== -1) {
        presentLadderCodes.push(d.code);
      }
    }

    /* No-op: exactly one ladder code present and it matches target. */
    if (presentLadderCodes.length === 1 && targetNorm && normalizeCode(presentLadderCodes[0]) === targetNorm) {
      return null;
    }
    /* No-op: no ladder codes present and no target. */
    if (presentLadderCodes.length === 0 && !targetCode) return null;

    /* Build the next state: drop ALL ladder codes (applicable or
       not — stale ones survive `applicable: false` and must be
       evicted), preserve every other code, then append target. */
    var keep = [];
    var seen = Object.create(null);
    for (var j = 0; j < existing.length; j++) {
      var raw = existing[j] && existing[j].code;
      if (!raw) continue;
      var norm = normalizeCode(raw);
      if (ourCodesNorm.indexOf(norm) !== -1) continue;  /* drop all ladder codes */
      if (seen[norm]) continue;
      seen[norm] = true;
      keep.push(String(raw).trim());
    }
    if (targetCode) keep.push(targetCode);

    return keep.join(',');  /* '' when keep is empty → intentional clear */
  };

  /* The only entry point that POSTs reconcile to /cart/update.js.
     Honors the single-writer mutex AND queues (never drops) reconcile
     requests that arrive while a mutation is in flight. */
  /* ── Manual section refresh after a 422 partial add ──────────
     Used by the _batchAddToCart catch handler when /cart/add.js
     returned 422 but some items did land (Shopify's "Only X items
     were added" partial-success case).

     The 422 response body is an error JSON, so the section HTML
     that cart-queue normally consumes is absent — its
     replaceCartSections + publishCartUpdate calls never fire.
     Without this manual refresh, cart-drawer and cart-icon-bubble
     stay frozen on their pre-add state even though Shopify's cart
     now has the items, so the customer sees a toast saying
     "Added X items" but an empty drawer.

     This fetches the two sections off `/cart?sections=...`,
     hands them to the shared replaceCartSections so cart-drawer
     and cart-page (whichever is mounted) patch in-place, then
     fires publishCartUpdate so downstream listeners — mini-cart,
     header bubble, sticky footer, account-orders rebuy badge —
     pick up the new cart state. Silent-fails: if the fetch or
     the helper is missing, the toast still surfaces and the
     drawer will catch up on its next interaction. */
  BundleBuilder.prototype._refreshCartSectionsAfterPartialAdd = function (cart) {
    var helpers = window.ddCartHelpers;
    return fetch('/cart?sections=' + encodeURIComponent('cart-drawer,cart-icon-bubble'), {
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (r) {
      if (!r.ok) throw new Error('section refresh failed (' + r.status + ')');
      return r.json();
    }).then(function (sections) {
      if (sections && helpers && typeof helpers.replaceCartSections === 'function') {
        try { helpers.replaceCartSections(sections, { scrollToTop: true }); } catch (_e) {}
      }
      if (cart && helpers && typeof helpers.publishCartUpdate === 'function') {
        try { helpers.publishCartUpdate(cart, SOURCE_TAG); } catch (_e) {}
      }
    }).catch(function (err) {
      console.warn('[dd-bundle-builder] partial-add section refresh failed', err);
    });
  };

  BundleBuilder.prototype._applyReconcile = function (cart, eligibleQty) {
    var helpers = window.ddCartHelpers;
    if (!helpers || typeof helpers.cartMutate !== 'function') return Promise.resolve(cart);

    var discountParam = this._reconcileDiscountCodes(cart, eligibleQty);
    if (discountParam === null) return Promise.resolve(cart);  /* no-op */

    /* Mutex queuing: in-flight mutation will re-check in its
       .finally(). Mark pending so it knows there's still work. */
    if (this.state._isMutating) {
      this._reconcilePending = true;
      return Promise.resolve(cart);
    }

    var self = this;
    self.state._isMutating = true;
    return helpers.cartMutate('/cart/update.js', { discount: discountParam }, {
      sections: SECTIONS_PARAM,
      action: 'bundle-reconcile',
      coalesceKey: 'bundle-reconcile',
      publishSource: SOURCE_TAG
    }).catch(function (err) {
      console.warn('[dd-bundle-builder] reconcile failed', err);
    }).finally(function () {
      self.state._isMutating = false;
      /* Drain queue: if a reconcile request arrived during the POST,
         re-fetch and re-run against the latest cart state. */
      if (self._reconcilePending) {
        self._reconcilePending = false;
        if (helpers.ensureFreshCart) {
          helpers.ensureFreshCart({ forceNetwork: true }).then(function (fresh) {
            var qty = bundleCollectionQty(fresh, self.eligibleVariantIds);
            self._applyReconcile(fresh, qty);
          }).catch(function () { /* fall through */ });
        }
      }
    });
  };

  BundleBuilder.prototype._bindEvents = function () {
    var self = this;

    /* Card → bundle event (bubbles from <dd-card2>). Filter by
       section ownership: only handle if the dispatching card is
       actually inside our grid. Otherwise a card on a future
       second instance would double-count. */
    document.addEventListener(EVENT_ADD, function (e) {
      var detail = e && e.detail;
      if (!detail || !detail.variant || !detail.variant.id) return;
      /* Confirm the originating card lives in our section. */
      var card = e.target;
      if (!card || !self.el.contains(card)) return;
      self._addItem(detail);
      /* Mobile UX: surface the panel so the customer SEES the line
         they just staged. On ≥768px the panel is sticky beside the
         grid and always in view, so the scroll is a no-op there. */
      self._maybeScrollPanelIntoView();
    });

    /* Panel interactions — delegated. */
    if (this.panel) {
      this.panel.addEventListener('click', function (e) {
        var ctaBtn = e.target.closest('[data-dd-bundle-add-all]');
        if (ctaBtn) {
          e.preventDefault();
          self._batchAddToCart();
          return;
        }
        /* "See our recommended stockup" popover toggle. The trigger
           lives below the CTA; we open/close on click and update
           aria-expanded so screen readers track state. */
        var recTrigger = e.target.closest('[data-dd-bundle-rec-trigger]');
        if (recTrigger) {
          e.preventDefault();
          self._toggleRecPopover(recTrigger);
          return;
        }
        var recClose = e.target.closest('[data-dd-bundle-rec-close]');
        if (recClose) {
          e.preventDefault();
          self._closeRecPopover();
          return;
        }

        /* Recommended-stockup row → Add to package. Reuses
           `_addItem(detail)` (the same entry point dd-card2 dispatches
           to) so the local bundle-state machine handles dedupe + qty
           increment + persist + render with no new code path. */
        var recAdd = e.target.closest('[data-dd-bundle-rec-add]');
        if (recAdd) {
          e.preventDefault();
          self._addFromRecPopover(recAdd);
          return;
        }
        var line = e.target.closest('[data-dd-bundle-line-id]');
        if (!line) return;
        var id = line.getAttribute('data-dd-bundle-line-id');
        if (e.target.closest('[data-dd-line-qty-inc]')) {
          e.preventDefault();
          self._incLine(id, 1);
        } else if (e.target.closest('[data-dd-line-qty-dec]')) {
          e.preventDefault();
          self._incLine(id, -1);
        } else if (e.target.closest('[data-dd-line-remove]')) {
          e.preventDefault();
          self._removeLine(id);
        }
      });

      /* Qty <input> direct edits — listen for `change` (fires on blur
         + Enter) rather than `input` (would mutate state on every
         keystroke and re-render mid-typing, eating the cursor). */
      this.panel.addEventListener('change', function (e) {
        var input = e.target.closest('[data-dd-line-qty-input]');
        if (!input) return;
        var line = input.closest('[data-dd-bundle-line-id]');
        if (!line) return;
        var id = line.getAttribute('data-dd-bundle-line-id');
        self._setLineQty(id, input.value);
      });

      /* Click-outside + Escape close the popover. We attach to
         document but check `self.panel.contains(target)` to avoid
         closing on clicks inside the popover itself, and skip when
         the popover isn't actually open (cheap guard, no work). */
      document.addEventListener('click', function (e) {
        var popover = self.panel.querySelector('[data-dd-bundle-rec-popover]');
        if (!popover || popover.hidden) return;
        if (popover.contains(e.target)) return;
        var trigger = self.panel.querySelector('[data-dd-bundle-rec-trigger]');
        if (trigger && trigger.contains(e.target)) return;
        self._closeRecPopover();
      });

      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var popover = self.panel.querySelector('[data-dd-bundle-rec-popover]');
        if (!popover || popover.hidden) return;
        self._closeRecPopover();
        var trigger = self.panel.querySelector('[data-dd-bundle-rec-trigger]');
        if (trigger) trigger.focus();
      });
    }

    /* Cart changes elsewhere on the page (drawer edits, PDP adds)
       should be reflected in our panel — UNLESS we caused them
       ourselves (source tag) OR we're mid-mutation (mutex). */
    document.addEventListener('cart:updated', function (e) {
      if (self.state._isMutating) return;
      var detail = e && e.detail;
      if (detail && detail.source === SOURCE_TAG) return;
      self._rehydrateFromCart();
    });

    /* Cross-tab consistency: when this tab refocuses, the cart in
       another tab may have changed. Single rehydrate covers it. */
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      if (self.state._isMutating) return;
      self._rehydrateFromCart();
    });
  };

  /* ── Hydration ────────────────────────────────────────────── */

  /* Inflates a stored items map into self.state.items with
     defensive coercion. Pulled out so the guest→customer migration
     path and the normal load path can share the same shape. */
  function inflateStoredItems(targetItems, storedItems) {
    if (!storedItems || typeof storedItems !== 'object') return;
    Object.keys(storedItems).forEach(function (id) {
      var line = storedItems[id];
      if (!line || !line.id) return;
      var priceCents = parseInt(line.priceCents, 10) || 0;
      var saleCents = parseInt(line.saleCents, 10);
      if (!Number.isFinite(saleCents) || saleCents <= 0) saleCents = priceCents;
      var compareAtCents = parseInt(line.compareAtCents, 10);
      if (!Number.isFinite(compareAtCents) || compareAtCents <= saleCents) compareAtCents = 0;
      targetItems[id] = {
        id: line.id,
        qty: Math.max(1, parseInt(line.qty, 10) || 1),
        productHandle: line.productHandle || '',
        productTitle: line.productTitle || '',
        imageUrl: line.imageUrl || '',
        options: Array.isArray(line.options) ? line.options : [],
        priceCents: priceCents,
        compareAtCents: compareAtCents,
        saleCents: saleCents
      };
    });
  }

  /* Returns true if a stored guest payload is past its TTL window. A
     missing savedAt is treated as legacy-but-fresh (pre-TTL writes
     existed before this change shipped — we don't want to drop those
     on first deploy). The next write re-stamps it. */
  function isGuestStoreExpired(stored) {
    if (!stored || typeof stored.savedAt !== 'number') return false;
    return (Date.now() - stored.savedAt) > GUEST_STORAGE_TTL_MS;
  }

  BundleBuilder.prototype._hydrate = function () {
    var self = this;

    /* 1a) One-time guest→customer migration. Runs only when the
       current request is signed in AND the customer's own storage
       slot is empty AND the guest slot has unexpired items. This
       lets a stylist build a package while logged out, sign in,
       and find their package intact. After migrating we clear the
       guest slot so a subsequent guest visit on the same device
       (different stylist on the same iPad) starts fresh. */
    if (!this._isPersistedKeyGuest && this.storageKey) {
      var customerStored = safeRead(this.storageKey);
      var hasCustomerItems = customerStored
        && customerStored.items
        && typeof customerStored.items === 'object'
        && Object.keys(customerStored.items).length > 0;
      if (!hasCustomerItems) {
        var guestStored = safeRead(GUEST_STORAGE_KEY);
        var guestHasItems = guestStored
          && guestStored.items
          && typeof guestStored.items === 'object'
          && Object.keys(guestStored.items).length > 0;
        if (guestHasItems && !isGuestStoreExpired(guestStored)) {
          inflateStoredItems(self.state.items, guestStored.items);
          /* Mirror into the customer slot via _persist so subsequent
             page loads find it under the customer key. We do this
             BEFORE the cart-cross-check so any drops there land in
             both layers consistently. */
          self._persist();
        }
        /* Either way, the guest slot has served its purpose for this
           signed-in customer — clear it. */
        try { localStorage.removeItem(GUEST_STORAGE_KEY); } catch (_e) {}
      }
    }

    /* 1b) Load panel composition from localStorage (cheap, sync). For
       guests this is the guest slot; for customers it's their own.
       Guest path also enforces the 7-day TTL. */
    if (this.storageKey) {
      var stored = safeRead(this.storageKey);
      if (this._isPersistedKeyGuest && isGuestStoreExpired(stored)) {
        /* Stale guest package — drop it and start fresh. A re-prefill
           is allowed after this point (treated as a fresh slot). */
        try { localStorage.removeItem(this.storageKey); } catch (_e) {}
        stored = null;
      }
      if (stored && stored.items && typeof stored.items === 'object') {
        /* Skip if 1a already populated state.items from the guest
           slot (signed-in migration path). Guard against double-load. */
        if (Object.keys(self.state.items).length === 0) {
          inflateStoredItems(self.state.items, stored.items);
        }
      }
    }

    /* 1c) Starter-package prefill. Seed the panel with the
       merchant-curated starter set whenever it would otherwise be
       empty:
         • prefill data was rendered (this.prefillItems present), AND
         • nothing was loaded/migrated above (state.items empty) —
           i.e. a fresh slot, an expired-and-dropped guest slot, or a
           slot the visitor cleared back to zero.
       Persisting immediately writes the starter lines to the slot, so
       a reload with items present won't re-seed (state.items is no
       longer empty); the set only comes back after the visitor clears
       everything and reloads. The inflated lines carry no
       maxQty/untracked flags, so _ensureCapsLoaded (called from
       _render) resolves stock caps for them exactly as it does for a
       localStorage-rehydrated package. */
    if (this.prefillItems
        && Object.keys(self.state.items).length === 0) {
      inflateStoredItems(self.state.items, this.prefillItems);
      self._persist();
    }

    /* 2) Reconcile with live cart — cart wins on conflict. */
    this._rehydrateFromCart().then(function () {
      self._render();
    }).catch(function () {
      /* Cart fetch failed — render what we have from localStorage. */
      self._render();
    });
  };

  BundleBuilder.prototype._rehydrateFromCart = function () {
    var self = this;
    var helpers = window.ddCartHelpers;
    var promise = (helpers && typeof helpers.ensureFreshCart === 'function')
      ? helpers.ensureFreshCart({ maxAge: CART_FRESH_MAX_AGE })
      : fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });

    return promise.then(function (cart) {
      if (!cart || !Array.isArray(cart.items)) return;
      /* Build a lookup of cart line qtys by variant id. */
      var cartQtys = Object.create(null);
      var cartPrices = Object.create(null);
      for (var i = 0; i < cart.items.length; i++) {
        var it = cart.items[i];
        if (!it || !it.variant_id) continue;
        var vid = String(it.variant_id);
        cartQtys[vid] = (cartQtys[vid] || 0) + (Number(it.quantity) || 0);
        if (it.price != null) cartPrices[vid] = Number(it.price);
      }
      /* Reconcile each panel item against cart.
         The panel is a STAGING area: items live in state + localStorage
         until the customer clicks "Add Package to Cart" (_batchAddToCart),
         which clears state and moves everything to the Shopify cart in one
         shot. So at any given moment a panel line is either:
           (a) staged-only — present in state, absent from cart, and the
               qty/price in state is authoritative; OR
           (b) coincidentally also in cart — rare in practice (the user
               added the same variant via PDP or cart drawer separately),
               in which case cart's qty/price is the canonical record and
               we adopt it.
         Earlier revisions dropped (a) entirely on every rehydrate, which
         is what wiped staged items on every reload, tab refocus, and
         foreign cart:updated event. Preserve (a). */
      var nextItems = Object.create(null);
      Object.keys(self.state.items).forEach(function (vid) {
        var line = self.state.items[vid];
        if (!line) return;
        if (cartQtys[vid]) {
          /* Live in cart — adopt cart qty + price. */
          nextItems[vid] = Object.assign({}, line, {
            qty: cartQtys[vid],
            priceCents: cartPrices[vid] != null ? cartPrices[vid] : line.priceCents
          });
        } else {
          /* Staged-only — preserve as-is so reloads/tab-switches don't
             nuke the customer's in-progress package. */
          nextItems[vid] = line;
        }
      });
      self.state.items = nextItems;

      /* Phase 2: reconcile ladder discount codes against current
         eligible cart qty. Promotes/demotes/clears as needed. The
         reconcile is the single integration point — it owns:
           • tier UP (30→60→100) — apply higher code, drop lower
           • tier DOWN (drop qty below a tier) — replace or clear
           • VIP-vs-bundle gate (bundlePct ≤ vipPct → no apply)
           • cleanup of stale codes (post-O3 BUNDLE20-at-60 ghost)
         All variations route through one POST to /cart/update.js. */
      var eligibleQty = bundleCollectionQty(cart, self.eligibleVariantIds);
      self._applyReconcile(cart, eligibleQty);
    }).then(function () {
      self._persist();
      self._render();
    });
  };

  /* ── Inventory cap lookup ──────────────────────────────────
     Resolve { maxQty, untracked } for a single variant, lazy-loading
     the product's full variant set via ddVariantLoader (sessionStorage-
     cached, GraphQL-backed, paginates past Liquid's 250 cap). Promise
     per-handle is memoized in this._inventoryCache so concurrent
     _addItem calls for the same product share one network round-trip.

     Returned shape:
       { maxQty: number|null, untracked: boolean }
     • untracked === true → no cap enforced (oversell allowed,
       inventory_management is null, or the variant fell off the
       loader's response). Treat as "type whatever, hope for the best."
     • untracked === false + maxQty=number → hard cap. line.qty
       clamps to maxQty; the + button disables at cap. maxQty can
       be 0 (variant became OOS between add and lookup) — the line
       is removed in that case.

     Failure modes (all degrade to untracked=true so the line still
     functions):
       • ddVariantLoader absent on the page
       • productHandle is blank / variant id not in loader response
       • GraphQL fetch errors / returns [] */
  BundleBuilder.prototype._lookupVariantCap = function (productHandle, variantId) {
    var self = this;
    var fallback = { maxQty: null, untracked: true };
    if (!productHandle) return Promise.resolve(fallback);
    if (!window.ddVariantLoader || typeof window.ddVariantLoader.fetch !== 'function') {
      return Promise.resolve(fallback);
    }
    if (!this._inventoryCache[productHandle]) {
      this._inventoryCache[productHandle] = window.ddVariantLoader
        .fetch(productHandle)
        .then(function (variants) {
          var map = Object.create(null);
          if (Array.isArray(variants)) {
            for (var i = 0; i < variants.length; i++) {
              var v = variants[i];
              if (!v || v.id == null) continue;
              var hasQty = typeof v.inventory_quantity === 'number';
              var continueSelling = v.inventory_policy === 'continue';
              map[String(v.id)] = {
                maxQty: hasQty ? v.inventory_quantity : null,
                untracked: !hasQty || continueSelling
              };
            }
          }
          return map;
        })
        .catch(function () { return Object.create(null); });
    }
    return this._inventoryCache[productHandle].then(function (map) {
      var info = map[String(variantId)];
      return info ? info : fallback;
    });
  };

  /* Apply a freshly-resolved cap to a line: clamp qty, drop the line
     if the variant is fully OOS, persist + re-render + status nudge.
     Idempotent — safe to call repeatedly with the same info. */
  BundleBuilder.prototype._applyCapToLine = function (vid, info) {
    var line = this.state.items[vid];
    if (!line) return false;
    line.maxQty = info.maxQty;
    line.untracked = !!info.untracked;
    if (line.untracked || typeof line.maxQty !== 'number') return false;
    if (line.maxQty <= 0) {
      var title = line.productTitle || 'That item';
      delete this.state.items[vid];
      this._persist();
      this._setStatus(title + ' is out of stock and was removed.', 'warn');
      return true;
    }
    if (line.qty > line.maxQty) {
      line.qty = line.maxQty;
      this._persist();
      this._setStatus(
        'Only ' + line.maxQty + ' of ' +
          (line.productTitle || 'that item') + ' in stock — qty capped.',
        'info'
      );
      return true;
    }
    return false;
  };

  /* Walk current lines and fire a cap lookup for any whose cap is
     unknown (typically: items rehydrated from localStorage/cart on
     first paint). Throttled-by-promise via _inventoryCache + a
     per-line `_capPending` flag so we don't re-fire while in flight. */
  BundleBuilder.prototype._ensureCapsLoaded = function () {
    var self = this;
    Object.keys(this.state.items).forEach(function (vid) {
      var line = self.state.items[vid];
      if (line.untracked === true) return;
      if (typeof line.maxQty === 'number') return;
      if (line._capPending) return;
      line._capPending = true;
      self._lookupVariantCap(line.productHandle, line.id).then(function (info) {
        var l = self.state.items[vid];
        if (!l) return;
        l._capPending = false;
        var clamped = self._applyCapToLine(vid, info);
        if (clamped) self._render();
      });
    });
  };

  /* ── State mutations (panel side) ─────────────────────────── */

  BundleBuilder.prototype._addItem = function (detail) {
    var v = detail.variant;
    var vid = String(v.id);
    var addQty = Number(detail.quantity) || 1;
    var self = this;
    /* Staging a new item closes out the post-add confirmation state.
       The customer is building a fresh package; the bento + "Package
       added" label would be stale and confusing. */
    this.state._justAddedPackage = false;
    var existing = this.state.items[vid];
    if (existing) {
      existing.qty += addQty;
    } else {
      /* priceCents      = gross/list price from Liquid (`v.price`)
         compareAtCents  = MSRP for strikethrough (null/0 if not on sale)
         saleCents       = customer-facing price (VIP-discounted via Regios)
         saleCents defaults to priceCents when no Regios resolution is
         available so non-VIP customers see a flat price (no strikethrough). */
      var saleCents = Number(v.saleCents);
      if (!Number.isFinite(saleCents) || saleCents <= 0) saleCents = Number(v.priceCents) || 0;
      var compareAtCents = Number(v.compareAtCents);
      if (!Number.isFinite(compareAtCents) || compareAtCents <= saleCents) compareAtCents = 0;
      this.state.items[vid] = {
        id: v.id,
        qty: addQty,
        productHandle: detail.productHandle || '',
        productTitle: detail.productTitle || '',
        imageUrl: detail.imageUrl || v.image || '',
        options: Array.isArray(v.options) ? v.options.filter(function (o) { return o; }) : [],
        priceCents: Number(v.priceCents) || 0,
        compareAtCents: compareAtCents,
        saleCents: saleCents,
        maxQty: null,        /* resolved by _lookupVariantCap below */
        untracked: true,     /* optimistic — switched off if loader returns a number */
        _capPending: true
      };
    }
    this._persist();
    this._render();

    /* Resolve the variant's stock cap and clamp asynchronously.
       Optimistic-add UX: the line appears immediately; the cap
       applies on the next microtask (cache hit) or the next
       network round-trip (first add for this product handle). */
    this._lookupVariantCap(detail.productHandle, v.id).then(function (info) {
      var line = self.state.items[vid];
      if (!line) return;
      line._capPending = false;
      self._applyCapToLine(vid, info);
      self._render();
    });
  };

  BundleBuilder.prototype._incLine = function (vid, delta) {
    var line = this.state.items[vid];
    if (!line) return;
    var next = line.qty + delta;
    if (next <= 0) { this._removeLine(vid); return; }
    /* Hard cap at variant inventory when tracked. If the user is
       already at cap and clicks + again, no-op + nudge — beats
       silently mutating and forcing them to discover the cap via
       the +button toggling between enabled/disabled. */
    if (!line.untracked && typeof line.maxQty === 'number') {
      if (next > line.maxQty) {
        if (line.qty >= line.maxQty) {
          this._setStatus(
            'Only ' + line.maxQty + ' of ' +
              (line.productTitle || 'that item') + ' in stock.',
            'info'
          );
          return;
        }
        next = line.maxQty;
        this._setStatus(
          'That\u2019s our full stock of ' +
            (line.productTitle || 'that item') + ' — qty set to ' + line.maxQty + '.',
          'info'
        );
      }
    }
    line.qty = next;
    this._persist();
    this._render();
  };

  /* Direct-set qty — used when a shopper types a value into the
     <input type="number"> in the qty stepper. Empty/invalid input is
     coerced to the prior qty (no-op) rather than zeroing the row out,
     because removal should be an explicit action via the trash icon. */
  BundleBuilder.prototype._setLineQty = function (vid, qty) {
    var line = this.state.items[vid];
    if (!line) return;
    var n = parseInt(qty, 10);
    if (isNaN(n) || n < 0) return;
    if (n === 0) { this._removeLine(vid); return; }
    if (!line.untracked && typeof line.maxQty === 'number' && n > line.maxQty) {
      n = line.maxQty;
      this._setStatus(
        'Only ' + line.maxQty + ' of ' +
          (line.productTitle || 'that item') + ' in stock — qty capped.',
        'info'
      );
    }
    if (n === line.qty) {
      /* Even on no-op, re-render so the input's typed value snaps
         back to the clamped/correct value (otherwise the field
         keeps whatever the shopper typed). */
      this._render();
      return;
    }
    line.qty = n;
    this._persist();
    this._render();
  };

  BundleBuilder.prototype._removeLine = function (vid) {
    if (!this.state.items[vid]) return;
    delete this.state.items[vid];
    this._persist();
    this._render();
  };

  BundleBuilder.prototype._persist = function () {
    if (!this.storageKey) return;
    /* savedAt is stamped on every write — used by _hydrate to TTL-
       expire stale guest packages. Customer entries carry the stamp
       too (cheap, future-proofs us if we ever want symmetric TTL
       there), but only the guest path acts on it. */
    safeWrite(this.storageKey, {
      items: this.state.items,
      savedAt: Date.now()
    });
  };

  /* ── Mobile auto-scroll on add ────────────────────────────────
     On <768px the panel stacks below the grid (it's not sticky
     there), so a customer who taps "Add to Package" on a card has
     no visual confirmation that the line landed — the panel is
     scrolled off-screen below them. Smooth-scroll the panel into
     view on every add. Guarded by:
       • viewport gate (≥768px = sticky panel, already visible)
       • already-visible gate (skip if panel top is in the upper
         60% of viewport, so subsequent adds don't re-hijack scroll
         once the user is parked on the panel)
       • prefers-reduced-motion (instant scroll instead of smooth)
       • feature detection on scrollTo({behavior:...}) for old iOS

     Header offset (`--header-height`) is published by the sticky
     header component; falls back to 80px on first paint. */
  BundleBuilder.prototype._maybeScrollPanelIntoView = function () {
    if (!this.panel) return;
    /* Mobile only — desktop has a sticky side panel. */
    if (typeof window.matchMedia === 'function' &&
        window.matchMedia('(min-width: 768px)').matches) return;

    var rect = this.panel.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    /* If the panel is already meaningfully in view (top within the
       upper 60% AND bottom above the fold-line), the customer can
       see it — don't yank scroll on every subsequent add. */
    if (rect.top >= 0 && rect.top < vh * 0.6 && rect.bottom > 0) return;

    var headerH = 80;
    try {
      var h = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      var n = parseFloat(h);
      if (Number.isFinite(n) && n > 0) headerH = n;
    } catch (_e) {}

    var top = (window.pageYOffset || document.documentElement.scrollTop || 0)
            + rect.top - headerH - 16;
    if (top < 0) top = 0;

    var prefersReduced = false;
    try {
      prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (_e) {}

    try {
      window.scrollTo({ top: top, behavior: prefersReduced ? 'auto' : 'smooth' });
    } catch (_e) {
      /* Old iOS Safari without scroll options support — fall back to
         the two-arg signature; loses smooth but lands in the right
         place. */
      window.scrollTo(0, top);
    }
  };

  /* ── Rendering ────────────────────────────────────────────── */

  BundleBuilder.prototype._render = function () {
    if (!this.linesEl || !this.lineTemplate) return;
    var ids = Object.keys(this.state.items);
    var totalQty = 0;
    /* Two parallel running totals so the panel can show a real
       strikethrough+sale UI like the card grid does:
         totalCents     = gross/MSRP (sum of qty × compareAtCents-or-priceCents)
         totalSaleCents = post-VIP price the customer actually pays
                          (sum of qty × saleCents)
       For lines with no compare/saleCents distinction (non-VIP
       customers, or pre-rollout legacy entries), the two sums are
       equal and the compare slot stays hidden. */
    var totalCents = 0;
    var totalSaleCents = 0;
    ids.forEach(function (vid) {
      var line = this.state.items[vid];
      totalQty += line.qty;
      var unitSale = line.saleCents || line.priceCents || 0;
      var unitGross = line.compareAtCents && line.compareAtCents > unitSale
        ? line.compareAtCents
        : (line.priceCents || unitSale);
      totalSaleCents += line.qty * unitSale;
      totalCents += line.qty * unitGross;
    }, this);
    this.state.totalQty = totalQty;
    this.state.totalCents = totalCents;
    this.state.totalSaleCents = totalSaleCents;

    /* Wipe existing lines, keep the empty placeholder hidden when
       we have items. */
    var existingLines = this.linesEl.querySelectorAll('[data-dd-bundle-line-id]');
    for (var i = 0; i < existingLines.length; i++) existingLines[i].remove();

    if (ids.length === 0) {
      if (this.emptyEl) this.emptyEl.hidden = false;
      /* Two empty states:
           • _justAddedPackage  → show the success confirmation bento
             (checkmark + "Package added to cart"). Set immediately
             after a successful batch-add and cleared the moment the
             customer stages a new item. Transient; not persisted.
           • default            → show the 3-row skeleton placeholders,
             the "ready, waiting" look from Figma. */
      var justAdded = !!this.state._justAddedPackage;
      if (this.confirmationEl) this.confirmationEl.hidden = !justAdded;
      if (this.skeletonsEl) this.skeletonsEl.hidden = justAdded;
    } else {
      if (this.emptyEl) this.emptyEl.hidden = true;
      if (this.skeletonsEl) this.skeletonsEl.hidden = true;
      if (this.confirmationEl) this.confirmationEl.hidden = true;
      ids.forEach(function (vid) {
        var line = this.state.items[vid];
        var node = this.lineTemplate.content.firstElementChild.cloneNode(true);
        node.setAttribute('data-dd-bundle-line-id', String(vid));

        var href = line.productHandle ? ('/products/' + line.productHandle) : '#';
        var titleText = line.productTitle || 'Item';

        var imgLink = node.querySelector('[data-dd-line-link]');
        if (imgLink) {
          imgLink.href = href;
          imgLink.setAttribute('aria-label', titleText);
        }

        var img = node.querySelector('[data-dd-line-img]');
        if (img && line.imageUrl) { img.src = line.imageUrl; img.alt = titleText; }

        var titleEl = node.querySelector('[data-dd-line-title]');
        if (titleEl) {
          titleEl.textContent = titleText;
          titleEl.href = href;
        }

        /* Options: render each value as a <span class="dd-cart-item__option">
           with a middot separator between them — same pattern dd-cart-item.liquid
           uses for `options_with_values`. Empty/blank values are skipped. */
        var metaEl = node.querySelector('[data-dd-line-meta]');
        if (metaEl) {
          var values = (line.options || []).filter(function (o) { return o; });
          metaEl.textContent = '';
          values.forEach(function (val, idx) {
            if (idx > 0) {
              var dot = document.createElement('span');
              dot.className = 'dd-cart-item__meta-dot';
              dot.setAttribute('aria-hidden', 'true');
              dot.textContent = '\u00B7';
              metaEl.appendChild(dot);
            }
            var span = document.createElement('span');
            span.className = 'dd-cart-item__option';
            span.textContent = val;
            metaEl.appendChild(span);
          });
        }

        var qtyInput = node.querySelector('[data-dd-line-qty-input]');
        if (qtyInput) {
          qtyInput.value = String(line.qty);
          if (!line.untracked && typeof line.maxQty === 'number') {
            qtyInput.setAttribute('max', String(line.maxQty));
          } else {
            qtyInput.removeAttribute('max');
          }
        }

        /* + button is disabled at cap. The HTML attribute is the
           authoritative signal — the click delegate above checks
           `closest('[data-dd-line-qty-inc]')` which still matches a
           disabled button, but the synthetic click never fires on
           a disabled <button>. _incLine's no-op nudge still handles
           keyboard-driven attempts (e.g. shopper holds Enter on the
           cap edge). */
        var incBtn = node.querySelector('[data-dd-line-qty-inc]');
        if (incBtn) {
          var atCap = !line.untracked
            && typeof line.maxQty === 'number'
            && line.qty >= line.maxQty;
          if (atCap) {
            incBtn.setAttribute('disabled', '');
            incBtn.setAttribute('aria-disabled', 'true');
            incBtn.setAttribute('title', 'No more in stock');
          } else {
            incBtn.removeAttribute('disabled');
            incBtn.setAttribute('aria-disabled', 'false');
            incBtn.removeAttribute('title');
          }
        }

        /* Price row mirrors the card grid: when the line has a
           higher gross than its customer-facing sale (VIP applied,
           or an MSRP compare-at), render BOTH spans — strikethrough
           gross on the left, active sale on the right — using the
           same `--original` / `--sale` modifier classes the cart
           drawer's dd-cart-item-page uses for visual parity. */
        var priceEl = node.querySelector('[data-dd-line-price]');
        var compareEl = node.querySelector('[data-dd-line-price-compare]');
        var unitSale = line.saleCents || line.priceCents || 0;
        var unitGross = line.compareAtCents && line.compareAtCents > unitSale
          ? line.compareAtCents
          : (line.priceCents || unitSale);
        var hasDiscount = unitGross > unitSale;
        if (priceEl) {
          priceEl.textContent = formatMoney(line.qty * unitSale);
          priceEl.classList.toggle('dd-cart-item__price--sale', hasDiscount);
        }
        if (compareEl) {
          if (hasDiscount) {
            compareEl.textContent = formatMoney(line.qty * unitGross);
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }

        /* Stock hint — only populated when the variant is tracked.
           At cap: "Max stock" + warn color. Within ~25% of cap:
           "Only N left". Otherwise hidden. The 25% threshold avoids
           noise on lines with deep inventory while still warning
           when supply is materially constrained. */
        var hintEl = node.querySelector('[data-dd-line-stock-hint]');
        if (hintEl) {
          if (!line.untracked && typeof line.maxQty === 'number' && line.maxQty > 0) {
            if (line.qty >= line.maxQty) {
              hintEl.hidden = false;
              hintEl.textContent = 'Max stock';
              hintEl.setAttribute('data-at-cap', 'true');
            } else if (line.maxQty - line.qty <= Math.max(2, Math.ceil(line.maxQty * 0.25))) {
              hintEl.hidden = false;
              hintEl.textContent = 'Only ' + line.maxQty + ' left';
              hintEl.setAttribute('data-at-cap', 'false');
            } else {
              hintEl.hidden = true;
              hintEl.textContent = '';
              hintEl.removeAttribute('data-at-cap');
            }
          } else {
            hintEl.hidden = true;
            hintEl.textContent = '';
            hintEl.removeAttribute('data-at-cap');
          }
        }

        this.linesEl.appendChild(node);
      }, this);
    }

    /* Lazy-load stock caps for any line that doesn't have one yet
       (rehydrate from localStorage/cart). Fires async; will re-call
       _render() once data lands and clamps occur. Idempotent per line. */
    this._ensureCapsLoaded();

    /* Threshold meter + progress text + savings. */
    this._renderMeter();
    this._renderProgress();
    this._renderRecPopoverButtons();

    this._renderTotals();

    /* CTA enable/disable + label.
       Three states:
         • items staged                 → enabled, "Add Package to Cart"
         • empty + just added           → disabled, "Package added"
         • empty + first-time / default → disabled, "Add Package to Cart"
       The "Adding…" transient is owned by _batchAddToCart and runs
       in between renders, so it doesn't need to be modeled here. */
    if (this.ctaEl) {
      var enabled = ids.length > 0;
      if (enabled) {
        this.ctaEl.removeAttribute('disabled');
        this.ctaEl.setAttribute('aria-disabled', 'false');
      } else {
        this.ctaEl.setAttribute('disabled', '');
        this.ctaEl.setAttribute('aria-disabled', 'true');
      }
    }
    if (this.ctaLabelEl) {
      if (ids.length === 0 && this.state._justAddedPackage) {
        this.ctaLabelEl.textContent = 'Package added';
      } else {
        /* Guest CTA label is rendered server-side as "Login to save"
           and lives on an <a>, not the button — bail when there's no
           [data-dd-bundle-add-all] button so we don't overwrite it. */
        if (this.ctaEl && this.ctaEl.hasAttribute('data-dd-bundle-add-all')) {
          this.ctaLabelEl.textContent = 'Add Package to Cart';
        }
      }
    }
  };

  /* Update the 3-stop meter fill width + per-stop is-passed/
     is-current classes. SSR-passed stops (where tier.pct ≤ vipPct)
     stay passed regardless of qty — VIP customers always see their
     already-earned tiers checked off. The fill width is anchored to
     the top tier's minQty (the meter denominator). */
  BundleBuilder.prototype._renderMeter = function () {
    if (!this.meterEl) return;  /* Inner Circle: meter not rendered */
    if (!Array.isArray(this.tiers) || !this.tiers.length) return;

    var qty = this.state.totalQty;
    var topMinQty = this.tiers[this.tiers.length - 1].minQty;
    var pct = topMinQty > 0 ? Math.min(100, Math.round((qty / topMinQty) * 100)) : 0;
    if (this.meterFillEl) this.meterFillEl.style.width = pct + '%';

    /* Walk all rendered stops. Each stop is "passed" purely when
       cart qty crosses its tier_min — under the new stacking model
       every non-IC tier is earnable by qty (no SSR-pre-checked
       tiers from VIP membership). IC customers never reach this
       method because their meter isn't rendered. */
    var stops = this.meterEl.querySelectorAll('[data-dd-bundle-meter-stop]');
    for (var i = 0; i < stops.length; i++) {
      var stop = stops[i];
      var stopMin = parseInt(stop.getAttribute('data-dd-bundle-meter-stop-min'), 10) || 0;
      var passed = qty >= stopMin;
      stop.classList.toggle('is-passed', passed);

      /* "Current" = the next stop the customer is working toward.
         A stop is current when it isn't passed AND the previous
         stop is passed (or this is the first stop). */
      var prevStop = (i > 0) ? stops[i - 1] : null;
      var prevMin = prevStop
        ? (parseInt(prevStop.getAttribute('data-dd-bundle-meter-stop-min'), 10) || 0)
        : 0;
      var prevPassed = !prevStop || qty >= prevMin;
      var isCurrent = !passed && prevPassed;
      stop.classList.toggle('is-current', isCurrent);
    }
  };

  /* Unified motivational progress line.

     Every tier's pct is rendered as the customer's EFFECTIVE combined
     savings (VIP × bundle), not the raw bundle code's pct. That way
     DeadHead at 30 units reads "10%" (their actual savings) rather
     than "5%" (the bundle code's nominal value). Matches the meter
     stop dots, the headline range, and the subtotal preview — one
     coherent number system across the panel.

     Decision matrix (qty = eligible bundle items in cart, pct values
     are the effective combined pct for the current vipPct):

       non-VIP (guest / logged_in, vipPct=0)  — pcts: 5 / 10 / 20
         qty < t1   → "Add N items to unlock 5% off this order."
         t1 ≤ qty   → "5% locked in. Add N more for 10%."
         t2 ≤ qty   → "10% locked in. Add N more for 20%."
         qty ≥ t3   → "Max 20% unlocked — nice stockup."

       deadhead (vipPct=5)  — pcts: 10 / 15 / 20
         qty < t1   → "Add N items to stack to 10% off this order."
         t1 ≤ qty   → "10% locked in. Add N more for 15%."
         t2 ≤ qty   → "15% locked in. Add N more for 20%."
         qty ≥ t3   → "Max 20% on this order."

       army (vipPct=10)  — pcts: 15 / 19 / 20
         qty < t1   → "Add N items to stack to 15% off this order."
         t1 ≤ qty   → "15% locked in. Add N more for 19%."
         t2 ≤ qty   → "19% locked in. Add N more for 20%."
         qty ≥ t3   → "Max 20% on this order."

       inner_circle  → no meter rendered, no progress text. */
  BundleBuilder.prototype._renderProgress = function () {
    if (!this.progressEl) return;
    if (!Array.isArray(this.tiers) || !this.tiers.length) return;

    var qty = this.state.totalQty;
    var t = this.tiers;
    var t1 = t[0] || null;
    var t2 = t[1] || null;
    var t3 = t[2] || null;
    var top = t[t.length - 1];
    var vip = this.vipPct;

    /* Convert each tier's nominal bundle pct → effective combined pct. */
    var p1 = t1 ? effectiveStackPct(vip, t1.pct) : 0;
    var p2 = t2 ? effectiveStackPct(vip, t2.pct) : 0;
    var p3 = t3 ? effectiveStackPct(vip, t3.pct) : 0;
    var pTop = top ? effectiveStackPct(vip, top.pct) : 0;

    var msg = '';
    var verb = vip > 0 ? 'stack to' : 'unlock';

    if (t1 && qty < t1.minQty) {
      msg = 'Add ' + (t1.minQty - qty) + ' items to ' + verb +
            ' ' + p1 + '% off this order.';
    } else if (t2 && qty < t2.minQty) {
      msg = p1 + '% locked in. Add ' + (t2.minQty - qty) +
            ' more for ' + p2 + '%.';
    } else if (t3 && qty < t3.minQty) {
      msg = p2 + '% locked in. Add ' + (t3.minQty - qty) +
            ' more for ' + p3 + '%.';
    } else {
      msg = 'Max ' + pTop + '% on this order — nice stockup.';
    }

    if (this.progressEl.textContent !== msg) this.progressEl.textContent = msg;
  };

  /* Effective combined-discount pct for (vipPct, bundlePct) under the
     new stacking strategy:

       • Inner Circle (vipPct >= 20)
            → 0 (no bundle ever applies, no preview)
       • bundlePct === 20 (any non-IC customer at 100+ units)
            → 20 (BUNDLE20 replaces VIP at the 20% storefront cap)
       • Otherwise (BUNDLE5 / BUNDLE10 stacking with DH or Army, or
         the no-VIP case where bundlePct = 0)
            → 100 * (1 - (1-vipPct/100)*(1-bundlePct/100))
              rounded to the nearest integer

     Multiplicative is correct because Shopify applies cart-level
     percent-off (BUNDLE5/10) on top of the VIP-discounted line price
     (Regios product-level discount). E.g. DeadHead (5%) at 60 units
     with BUNDLE10: gross $100 → after VIP $95 → after BUNDLE10
     $85.50 → 14.5% saved → rounds to 15% in the preview. */
  function effectiveStackPct(vipPct, bundlePct) {
    if (vipPct >= 20) return 0;
    if (bundlePct >= 20) return 20;
    var factorVip = (100 - vipPct) / 100;
    var factorBundle = (100 - bundlePct) / 100;
    var savedFrac = 1 - factorVip * factorBundle;
    return Math.round(savedFrac * 100);
  }

  /* Unified totals renderer — owns subtotal, compare (struck-through
     gross), and the savings line as a single coherent UI.

     Effective pct comes from effectiveStackPct() above. When > 0:

       Subtotal  $3,000.00  $2,550.00      ← compare struck through left;
                                              discounted total primary right
       You save 15% — $450.00              ← unified savings line

     IC customers never get a preview (effective = 0 by definition).
     Their actual cart subtotal will reflect their VIP discount via
     Regios at paint time; we don't double-render it in the panel. */
  BundleBuilder.prototype._renderTotals = function () {
    if (!this.subtotalEl) return;
    var gross = this.state.totalCents;
    var afterVip = this.state.totalSaleCents != null
      ? this.state.totalSaleCents
      : gross;
    var resetCompare = function () {
      if (this.subtotalCompareEl) {
        this.subtotalCompareEl.hidden = true;
        this.subtotalCompareEl.textContent = '$0.00';
      }
      this.subtotalEl.classList.remove('is--discounted');
      if (this.savingsEl) this.savingsEl.hidden = true;
    }.bind(this);

    /* No items → render zero, no compare. */
    if (gross <= 0) {
      this.subtotalEl.textContent = formatMoney(gross);
      resetCompare();
      return;
    }

    /* Two discount sources collapse into one effective discounted total:
         1. VIP (per-line saleCents — already aggregated as afterVip).
            For IC this is gross × 0.80; for Army × 0.90; etc.
         2. Bundle tier (cart-level, only earned past the qty threshold).
            Combined % off gross comes from effectiveStackPct(vip, bundle).
            stack_replace_higher: bundle only "wins" when bundlePct >
            vipPct. For IC, effectiveStackPct returns 0 by definition
            (the bundle ladder never beats the permanent 20%), so we
            fall through to the VIP-only branch and IC sees their 20%
            preview directly from afterVip — matching the card grid. */
    var resolved = this._resolveTier(this.state.totalQty);
    var bundlePct = resolved ? resolved.pct : 0;
    var combinedPct = effectiveStackPct(this.vipPct, bundlePct);

    var discountedCents;
    if (combinedPct > 0 && bundlePct > this.vipPct) {
      /* Bundle tier earned and exceeds VIP — apply the combined %
         off gross directly. (Bundle either replaces VIP at 20% or
         stacks multiplicatively for non-IC sub-20% tiers.) */
      discountedCents = gross - Math.round(gross * (combinedPct / 100));
    } else {
      /* No bundle tier (or VIP already meets/beats it) — the
         per-line VIP price is the authoritative discounted total. */
      discountedCents = afterVip;
    }

    if (discountedCents >= gross) {
      /* No saving to surface (no VIP, no bundle) — flat subtotal. */
      this.subtotalEl.textContent = formatMoney(gross);
      resetCompare();
      return;
    }

    var saveCents = gross - discountedCents;
    var savedPct = Math.round((saveCents / gross) * 100);

    this.subtotalEl.textContent = formatMoney(discountedCents);
    this.subtotalEl.classList.add('is--discounted');
    if (this.subtotalCompareEl) {
      this.subtotalCompareEl.textContent = formatMoney(gross);
      this.subtotalCompareEl.hidden = false;
    }
    if (this.savingsEl && this.savingsAmountEl) {
      this.savingsAmountEl.textContent = formatMoney(saveCents);
      if (this.savingsPctEl) this.savingsPctEl.textContent = savedPct + '%';
      this.savingsEl.hidden = false;
    }
  };

  /* Build a bundle-add payload from a recommended-stockup row button
     and route it to `_addItem`. The row carries the variant id, color,
     priceCents, and image URL as data-* attrs — we synthesize the same
     shape `<dd-card2>` dispatches on `dd-card2:add-to-bundle` so the
     downstream state code is identical. */
  BundleBuilder.prototype._addFromRecPopover = function (btn) {
    /* Belt-and-suspenders: ddVariantLoader strips data-dd-bundle-rec-add
       from OOS buttons so the click delegate won't match, but if a
       race-y interaction (e.g. focus + Enter mid-load) reaches this
       method anyway, refuse the add. */
    if (btn.disabled || btn.classList.contains('dd-bundle-builder__panel-rec-add--oos')) return;
    var vid = btn.getAttribute('data-dd-rec-variant-id');
    var color = btn.getAttribute('data-dd-rec-color') || '';
    var priceCents = parseInt(btn.getAttribute('data-dd-rec-price-cents'), 10);
    var image = btn.getAttribute('data-dd-rec-image') || '';
    if (!vid) return;

    var detail = {
      variant: {
        id: Number(vid),
        options: ['22"', color, 'Straight'],
        priceCents: isNaN(priceCents) ? 0 : priceCents,
        image: image
      },
      productHandle: 'superweft',
      productTitle: 'SuperWeft',
      imageUrl: image,
      quantity: 1
    };
    this._addItem(detail);

    /* "Added" state is now driven by actual bundle membership, not a
       transient post-click timer. _addItem → _render → _renderRecPopoverButtons
       toggles is--added for every rec-popover row whose variant id is
       currently in this.state.items, so the checkmark persists as long
       as the item is in the bundle and reverts when the shopper
       removes it. */
  };

  /* Sync the recommended-stockup popover rows with current bundle
     membership. Walks every [data-dd-bundle-rec-add] button and
     toggles `is--added` based on whether its variant id is in
     this.state.items. OOS buttons (already stripped of the data attr
     by ddVariantLoader on init) are skipped naturally because they
     no longer match the selector. */
  BundleBuilder.prototype._renderRecPopoverButtons = function () {
    if (!this.panel) return;
    var btns = this.panel.querySelectorAll('[data-dd-bundle-rec-add]');
    if (!btns.length) return;
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      var vid = b.getAttribute('data-dd-rec-variant-id');
      var inBundle = !!(vid && this.state.items[vid]);
      b.classList.toggle('is--added', inBundle);
      b.setAttribute('aria-pressed', inBundle ? 'true' : 'false');
    }
  };

  /* ── Recommended-stockup popover ─────────────────────────────
     Open/close helpers for the small info popover beneath the CTA.
     Encapsulated so a future variant (e.g. tooltip on hover for
     pointer devices) can be added without rewriting bind logic. */
  BundleBuilder.prototype._toggleRecPopover = function (trigger) {
    var popover = this.panel.querySelector('[data-dd-bundle-rec-popover]');
    if (!popover) return;
    if (popover.hidden) {
      popover.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      /* Lazy-fetch SuperWeft availability the first time the
         popover opens. Liquid couldn't gate stock at render time
         (product.variants caps at 250; SuperWeft has 1000+), so
         we use the Storefront GraphQL-backed ddVariantLoader. The
         loader caches in sessionStorage, so re-opens are O(1). */
      this._loadRecPopoverStock();
    } else {
      this._closeRecPopover();
    }
  };

  /* Mark popover rows whose SuperWeft 22"/Straight variant is out
     of stock. Runs at most once per page load (well, once per
     sessionStorage cache window — 5 minutes via ddVariantLoader).
     If the loader isn't on the page or the fetch fails, every
     button stays enabled and the safety net is /cart/add.js
     server-side validation. */
  BundleBuilder.prototype._loadRecPopoverStock = function () {
    if (this._recPopoverStockLoaded) return;
    if (!window.ddVariantLoader || typeof window.ddVariantLoader.fetch !== 'function') return;
    var self = this;
    this._recPopoverStockLoaded = true;

    window.ddVariantLoader.fetch('superweft').then(function (variants) {
      if (!Array.isArray(variants) || !variants.length) return;
      /* Build a Set of in-stock variant IDs (as strings). The
         loader normalizes Storefront GIDs to numeric IDs, so this
         matches the data-dd-rec-row-vid attribute directly. */
      var inStock = Object.create(null);
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (v && v.available && v.id != null) inStock[String(v.id)] = true;
      }
      var rows = self.panel ? self.panel.querySelectorAll('[data-dd-rec-row]') : [];
      for (var r = 0; r < rows.length; r++) {
        var row = rows[r];
        var vid = row.getAttribute('data-dd-rec-row-vid');
        if (!vid) continue;
        if (inStock[vid]) continue;
        /* OOS: remove the click-trigger attr so the panel's click
           delegate won't match, swap the button to disabled +
           --oos modifier, and reveal the pre-rendered "Sold out"
           label (Default/Added labels stay hidden via CSS). */
        row.classList.add('dd-bundle-builder__panel-rec-popover-row--oos');
        var btn = row.querySelector('[data-dd-bundle-rec-add]');
        if (!btn) continue;
        btn.removeAttribute('data-dd-bundle-rec-add');
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('dd-bundle-builder__panel-rec-add--oos');
        var color = btn.getAttribute('data-dd-rec-color') || '';
        btn.setAttribute('aria-label', '22 inch ' + color + ' is sold out');
      }
    }).catch(function () { /* fail open — server validation catches OOS adds */ });
  };

  BundleBuilder.prototype._closeRecPopover = function () {
    var popover = this.panel.querySelector('[data-dd-bundle-rec-popover]');
    var trigger = this.panel.querySelector('[data-dd-bundle-rec-trigger]');
    if (popover) popover.hidden = true;
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  };

  BundleBuilder.prototype._setStatus = function (msg, kind) {
    if (!this.statusEl) return;
    if (!msg) {
      this.statusEl.textContent = '';
      this.statusEl.hidden = true;
      this.statusEl.removeAttribute('data-dd-bundle-status-kind');
      return;
    }
    this.statusEl.textContent = msg;
    this.statusEl.hidden = false;
    this.statusEl.setAttribute('data-dd-bundle-status-kind', kind || 'info');
  };

  /* ── Batch add ────────────────────────────────────────────── */

  BundleBuilder.prototype._batchAddToCart = function () {
    var self = this;
    var helpers = window.ddCartHelpers;
    if (!helpers || typeof helpers.cartMutate !== 'function') {
      self._setStatus('Cart unavailable — please refresh and try again.', 'error');
      return;
    }
    var ids = Object.keys(self.state.items);
    if (!ids.length || self.state._isMutating) return;

    self.state._isMutating = true;
    if (self.ctaEl) self.ctaEl.setAttribute('disabled', '');
    if (self.ctaLabelEl) self.ctaLabelEl.textContent = 'Adding…';
    self._setStatus('', '');

    /* ── Stock-aware partial add ──────────────────────────────
       Bundle lines stage with `maxQty` = TOTAL inventory for the
       variant. If the cart already contains some of that variant,
       Shopify rejects the whole `/cart/add.js` call atomically
       when `cart_qty + add_qty > maxQty` for ANY line — even if
       the other lines would have succeeded.

       Pre-flight: fetch fresh cart, subtract in-cart qty from
       each line's maxQty to get `roomLeft`, clamp the add qty
       to roomLeft (≥0). Items clamped to 0 are dropped entirely.
       The trim outcome is stashed on `self._pendingTrimInfo` so
       the success-handler can surface a precise toast instead
       of the generic "Added to cart." */
    self._pendingTrimInfo = null;

    /* Preflight cart read. forceNetwork because a stale cache
       (e.g. user clicked Add Package, the post-add `cart:updated`
       hasn't fired yet, or another tab mutated the cart) causes
       the per-line `roomLeft` math below to under-count what's
       already in the cart — Shopify then rejects the whole batch
       atomic-style with a 422. The catch path below DOES re-fetch
       and partial-recover, but a forced preflight read here turns
       the failure mode into a graceful pre-clamp instead of a
       round-trip to a 422.

       Cost: +1 GET /cart.js per "Add Package to Cart" click. This
       is a high-intent deliberate action — not a hot path — so the
       extra request is well within the rate-limit budget per
       .cursor/rules/rate-limit-awareness.mdc. */
    helpers.ensureFreshCart({ forceNetwork: true }).then(function (cart) {
      /* Capture pre-add cart count so the catch handler can detect
         partial success (Shopify sometimes adds what fits + returns
         422 with "Only X items were added"). */
      self._preflightCartItemCount = (cart && cart.item_count) || 0;
      self._preflightCartQtyByVid = null;  /* populated below */
      /* Map current cart qtys by variant id. /cart.js returns
         `variant_id` on each line, but legacy responses sometimes
         use `id` (line item id == variant id when product has no
         line item properties differentiator). We prefer variant_id
         and fall back to id. */
      var cartQtyByVid = Object.create(null);
      if (cart && Array.isArray(cart.items)) {
        cart.items.forEach(function (li) {
          var vid = String(li.variant_id || li.id);
          cartQtyByVid[vid] = (cartQtyByVid[vid] || 0) + (li.quantity || 0);
        });
      }
      self._preflightCartQtyByVid = cartQtyByVid;

      var clampedItems = [];
      var trimmed = [];      /* lines that added < desired */
      var fullyBlocked = []; /* lines clamped to 0 */

      ids.forEach(function (vid) {
        var line = self.state.items[vid];
        if (!line || !line.id) return;
        var desired = line.qty || 0;
        if (desired <= 0) return;

        var inCart = cartQtyByVid[String(line.id)] || 0;
        /* Untracked variants or missing maxQty → no cap, Shopify
           is the final authority (it'll accept any quantity for
           "continue selling when out of stock" items). */
        var unlimited = line.untracked || typeof line.maxQty !== 'number';
        var roomLeft = unlimited ? Infinity : Math.max(0, line.maxQty - inCart);
        var effective = Math.min(desired, roomLeft);

        if (effective <= 0) {
          fullyBlocked.push({ title: line.productTitle || 'that item', desired: desired });
        } else {
          clampedItems.push({ id: line.id, quantity: effective });
          if (effective < desired) {
            trimmed.push({
              title: line.productTitle || 'that item',
              desired: desired,
              effective: effective,
              roomLeft: roomLeft
            });
          }
        }
      });

      /* Nothing to add → bail before hitting /cart/add.js. We
         throw a sentinel error the catch-handler can detect so
         it doesn't fall through to the generic "Something went
         wrong" toast. */
      if (!clampedItems.length) {
        var noneErr = new Error('DD_BUNDLE_BLOCKED_BY_CART');
        noneErr.ddBundleBlocked = true;
        noneErr.fullyBlocked = fullyBlocked;
        throw noneErr;
      }

      self._pendingTrimInfo = { trimmed: trimmed, fullyBlocked: fullyBlocked };

      return helpers.cartMutate('/cart/add.js', { items: clampedItems }, {
        sections: SECTIONS_PARAM,
        action: 'bundle-batch-add',
        coalesceKey: 'bundle-add',
        publishSource: SOURCE_TAG
      });
    }).then(function () {
      /* /cart/add.js response is line items, not the full cart.
         Re-read the cart to get discount_codes + threshold check. */
      return helpers.ensureFreshCart({ forceNetwork: true });
    }).then(function (cart) {
      /* Phase 2: reconcile ladder discount codes. Release the
         single-writer mutex BEFORE invoking reconcile — reconcile
         acquires its own. Otherwise _applyReconcile sees the held
         mutex, queues, and the .finally() drain path race-condition
         windows around our own .finally() below. */
      self.state._isMutating = false;
      var eligibleQty = bundleCollectionQty(cart, self.eligibleVariantIds);
      var resolved = self._resolveTier(eligibleQty);
      /* Capture the eligible state for the status message. The
         reconcile fires async; status text reflects the intended
         end-state (which Shopify will confirm or warn about via
         the rejected-toast path on its own update response). */
      var willApply = !!resolved && resolved.pct > self.vipPct;
      return self._applyReconcile(cart, eligibleQty).then(function () {
        return { applied: willApply, tier: resolved };
      });
    }).then(function (outcome) {
      /* Open the cart drawer to confirm. */
      try {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open(self.ctaEl);
      } catch (_e) {}
      /* Clear panel state — the items now live in the cart, and
         on next render we'll re-hydrate from cart. */
      self.state.items = Object.create(null);
      /* Transient confirmation flag — _render reads this to swap the
         skeleton placeholders for the success bento + flip the CTA
         label to "Package added". Cleared the moment _addItem stages
         the next product, or on page reload (not persisted). */
      self.state._justAddedPackage = true;
      self._persist();

      /* Trim-aware toast.
         If pre-flight clamped any lines, surface that as the primary
         message — the customer needs to know they got less than they
         staged. The discount/VIP message is logged to console but
         doesn't compete for the status slot. If nothing was trimmed,
         the discount/VIP message wins as before. */
      var trim = self._pendingTrimInfo;
      self._pendingTrimInfo = null;

      var msg;
      var kind = 'success';

      if (trim && (trim.trimmed.length || trim.fullyBlocked.length)) {
        kind = 'warn';
        if (trim.trimmed.length === 1 && !trim.fullyBlocked.length) {
          /* Single line trimmed, others (if any) fully fit. Use the
             precise "Only X more of [item] available — added X" copy. */
          var t = trim.trimmed[0];
          msg = 'Only ' + t.effective + ' more of ' + t.title +
            ' available — added ' + t.effective + '.';
        } else if (trim.trimmed.length === 0 && trim.fullyBlocked.length === 1) {
          /* One line fully blocked, others added. */
          msg = trim.fullyBlocked[0].title + ' is already at max stock in your cart — others added.';
        } else {
          /* Multiple lines affected — give a summary. */
          var affected = trim.trimmed.length + trim.fullyBlocked.length;
          msg = affected + ' items adjusted to available stock — rest added to cart.';
        }
      } else if (outcome && outcome.applied && outcome.tier) {
        msg = 'Added to cart with ' + outcome.tier.pct + '% off applied.';
      } else if (self.vipPct > 0) {
        msg = 'Added to cart with your ' + self.vipPct + '% VIP pricing.';
      } else {
        msg = 'Added to cart.';
      }
      self._setStatus(msg, kind);
    }).catch(function (err) {
      /* Pre-flight "everything is already maxed in cart" sentinel.
         Surface a friendly explanation rather than the generic error
         toast, and DON'T clear panel state — the customer's staged
         items remain so they can adjust qty and retry. */
      if (err && err.ddBundleBlocked) {
        var blocked = err.fullyBlocked || [];
        var blockedMsg;
        if (blocked.length === 1) {
          blockedMsg = blocked[0].title + ' is already at max stock in your cart — nothing added.';
        } else {
          blockedMsg = 'Those items are already at max stock in your cart — nothing added.';
        }
        self._setStatus(blockedMsg, 'warn');
        self._pendingTrimInfo = null;
        return;
      }

      /* Shopify stock-shaped 422.
         /cart/add.js can return 422 with "Only X items were added
         to your cart due to availability." or "The maximum quantity
         of this item is already in your cart." — these are partial-
         success or stock-cap signals, NOT generic failures. Re-fetch
         the cart, compare to the pre-add snapshot, and:
           • If anything landed → treat as partial success: clear
             staged state, open drawer, run reconcile, surface a
             stock-aware toast.
           • If nothing landed → preserve staged state so the user
             can lower qty and retry, surface the stock message. */
      var parsed = helpers.parseCartStockError
        ? helpers.parseCartStockError(err, { operation: 'add' })
        : null;

      if (parsed) {
        return helpers.ensureFreshCart({ forceNetwork: true }).then(function (cart) {
          var beforeCount = self._preflightCartItemCount || 0;
          var afterCount = (cart && cart.item_count) || 0;
          var landed = afterCount - beforeCount;

          var stockMsg = helpers.stockErrorMessage
            ? (helpers.stockErrorMessage(parsed, { operation: 'add' }) || {}).message
            : null;

          if (landed > 0) {
            /* Partial success. Mirror the happy-path side-effects:
               refresh the cart sections, open the drawer, clear
               staged state (cart is now the truth), reconcile the
               discount ladder, then surface the stock-aware toast.

               Why the manual section refresh:
                 The 422 response body is an error JSON — no section
                 HTML. cart-queue's success path (which normally calls
                 replaceCartSections + publishCartUpdate) is skipped on
                 a rejection. The reconcile that follows might also be
                 a no-op (when no tier boundary is crossed), so we
                 can't rely on it to refresh the drawer. Fetch the
                 cart-drawer + cart-icon-bubble sections explicitly and
                 patch them in BEFORE opening the drawer — otherwise
                 the user sees an empty/stale drawer despite the items
                 being in the actual Shopify cart. */
            return self._refreshCartSectionsAfterPartialAdd(cart).then(function () {
              try {
                var drawer = document.querySelector('cart-drawer');
                if (drawer && typeof drawer.open === 'function') drawer.open(self.ctaEl);
              } catch (_e) {}
              self.state.items = Object.create(null);
              /* Partial success still counts — flip the panel to the
                 "Package added" confirmation state. */
              self.state._justAddedPackage = true;
              self._persist();
              self._pendingTrimInfo = null;

              var eligibleQty = bundleCollectionQty(cart, self.eligibleVariantIds);
              return self._applyReconcile(cart, eligibleQty).then(function () {
                self._setStatus(
                  stockMsg || ('Added ' + landed + ' items — others exceeded available stock.'),
                  'warn'
                );
              });
            });
          }

          /* Nothing landed — preserve staged state so the user can
             adjust qty and retry. */
          self._pendingTrimInfo = null;
          self._setStatus(
            stockMsg || 'Those items exceed available stock — try a lower quantity.',
            'warn'
          );
        });
      }

      console.warn('[dd-bundle-builder] batch add failed', err);
      self._pendingTrimInfo = null;
      self._setStatus('Something went wrong — please try again.', 'error');
    }).finally(function () {
      /* Belt-and-suspenders: if we threw before the explicit release
         above, ensure the mutex still resets. The reconcile manages
         its own lifecycle, so no-op when we're already clear. The CTA
         label is now owned by _render — it picks "Package added" /
         "Add Package to Cart" based on state._justAddedPackage. */
      self.state._isMutating = false;
      self._render();
    });
  };

  /* ── Boot ─────────────────────────────────────────────────── */

  ready(function () {
    var sections = document.querySelectorAll('[data-dd-bundle-builder]');
    if (!sections.length) return;
    for (var i = 0; i < sections.length; i++) {
      try { new BundleBuilder(sections[i]); }
      catch (err) { console.warn('[dd-bundle-builder] init failed', err); }
    }
  });
})();
